import http from './http';

// ── Settings / signup control ───────────────────────────────────

export interface PortalSettings {
  signup_enabled: boolean;
  signup_message: string | null;
  launch_at: string | null;
  newsletter_open: boolean;
  updated_at: string | null;
}

export interface PublicSignupState {
  signup_enabled: boolean;
  signup_message: string | null;
  launch_at: string | null;
  newsletter_open: boolean;
}

// ── Products ────────────────────────────────────────────────────

export interface PortalProduct {
  id: string;
  name: string;
  description: string | null;
  kind: 'product' | 'plan' | 'bundle';
  sku: string | null;
  price_cents: number;
  currency: string;
  billing_interval: string | null;
  module_key: string | null;
  stock: number | null;
  is_active: boolean;
  stripe_price_id: string | null;
  created_at: string | null;
}

// ── Billing analytics ───────────────────────────────────────────

export interface BillingOverview {
  total_users: number;
  active_subscriptions: number;
  total_subscriptions: number;
  canceled_subscriptions: number;
  past_due_subscriptions: number;
  revenue_cents: number;
  revenue_this_month_cents: number;
  payment_count: number;
  average_payment_cents: number;
  mrr_cents: number;
  revenue_verified: boolean;
}

export interface RevenuePoint { date: string; revenue_cents: number; payments: number }

export interface BillingAnalytics {
  overview: BillingOverview;
  revenue_series: RevenuePoint[];
  subscriptions: {
    by_status: Array<{ status: string; count: number }>;
    by_plan: Array<{ plan_key: string; plan_name: string; count: number }>;
  };
  product_sales: Array<{ product: string; plan_key: string | null; units: number; revenue_cents: number }>;
}

// ── Customers ───────────────────────────────────────────────────

export interface CustomerRow {
  id: string;
  username: string;
  email: string;
  is_owner: boolean;
  is_active: boolean;
  created_at: string | null;
  subscriptions: number;
  active_subscriptions: number;
  invoices: number;
  lifetime_value_cents: number;
}

export interface CustomerDetail {
  id: string;
  username: string;
  email: string;
  created_at: string | null;
  lifetime_value_cents: number;
  subscriptions: Array<{ id: string; plan_key: string; plan_name: string; status: string; started_at: string | null; current_period_end: string | null }>;
  payments: Array<{ id: string; event_type: string; amount_cents: number | null; currency: string | null; status: string | null; created_at: string | null }>;
  invoices: PortalInvoice[];
  payment_methods: PaymentMethod[];
  payment_methods_note: string;
}

export interface PaymentMethod {
  id: string;
  user_id: string;
  provider: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  created_at: string | null;
}

// ── Invoices ────────────────────────────────────────────────────

export interface PortalInvoice {
  id: string;
  number: string;
  user_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  product_id: string | null;
  plan_key: string | null;
  kind: string;
  amount_cents: number;
  currency: string;
  status: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  line_items?: Array<{ description?: string; label?: string; quantity?: number; amount_cents?: number }>;
  note: string | null;
}

// ── Newsletters / campaigns ─────────────────────────────────────

export interface Subscriber {
  id: string;
  email: string;
  name: string | null;
  status: string;
  source: string | null;
  created_at: string | null;
}

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  body: string | null;
  kind: string;
  status: string;
  audience: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  detail: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string | null;
}

/** True only for an address we could actually mail a support link to.
 *  Rejects blanks and the signup/platform placeholder domains. */
export function hasUsableEmail(email: string | null | undefined): boolean {
  const value = (email || '').trim().toLowerCase();
  if (!value || !value.includes('@')) return false;
  const [local, domain] = value.split('@');
  if (!local || !domain || !domain.includes('.')) return false;
  return !(domain.endsWith('superaikingdom.com') || domain.endsWith('local.saik'));
}

export interface SupportLinkResult {
  ok: boolean;
  emailed_to: string;
  expires_at: string;
  expires_minutes: number;
}

export const customerPortalApi = {
  // settings
  getSettings: () => http.get<PortalSettings>('/customer-portal/settings').then(r => r.data),
  updateSettings: (data: Partial<Omit<PortalSettings, 'updated_at'>>) =>
    http.put<PortalSettings>('/customer-portal/settings', data).then(r => r.data),

  // products
  listProducts: () => http.get<{ products: PortalProduct[] }>('/customer-portal/products').then(r => r.data.products),
  createProduct: (data: Partial<PortalProduct>) =>
    http.post<PortalProduct>('/customer-portal/products', data).then(r => r.data),
  updateProduct: (id: string, data: Partial<PortalProduct>) =>
    http.put<PortalProduct>(`/customer-portal/products/${id}`, data).then(r => r.data),
  deleteProduct: (id: string) => http.delete(`/customer-portal/products/${id}`).then(r => r.data),

  // billing
  billingAnalytics: (days = 30) =>
    http.get<BillingAnalytics>('/customer-portal/billing/analytics', { params: { days } }).then(r => r.data),

  // customers
  listCustomers: (search?: string) =>
    http.get<{ customers: CustomerRow[] }>('/customer-portal/customers', { params: search ? { search } : {} }).then(r => r.data.customers),
  customerDetail: (id: string) =>
    http.get<CustomerDetail>(`/customer-portal/customers/${id}`).then(r => r.data),
  sendSupportLink: (userId: string, expiresMinutes = 30) =>
    http.post<SupportLinkResult>(`/customer-portal/customers/${userId}/support-link`, { expires_minutes: expiresMinutes }).then(r => r.data),
  savePaymentMethod: (data: Partial<PaymentMethod> & { user_id: string }) =>
    http.post<PaymentMethod>('/customer-portal/payment-methods', data).then(r => r.data),

  // invoices
  listInvoices: () => http.get<{ invoices: PortalInvoice[] }>('/customer-portal/invoices').then(r => r.data.invoices),
  createInvoice: (data: Record<string, unknown>) =>
    http.post<PortalInvoice>('/customer-portal/invoices', data).then(r => r.data),
  markInvoicePaid: (id: string) =>
    http.post<PortalInvoice>(`/customer-portal/invoices/${id}/mark-paid`).then(r => r.data),
  downloadInvoicePdf: async (id: string, number: string) => {
    const { downloadBlob } = await import('../utils/download');
    const r = await http.get(`/customer-portal/invoices/${id}/pdf`, { responseType: 'blob' });
    downloadBlob(new Blob([r.data], { type: 'application/pdf' }), `invoice-${number}.pdf`);
  },

  // newsletter
  listSubscribers: () =>
    http.get<{ subscribers: Subscriber[]; subscribed_count: number }>('/customer-portal/newsletter/subscribers').then(r => r.data),
  addSubscriber: (email: string, name?: string) =>
    http.post('/customer-portal/newsletter/subscribers', { email, name }).then(r => r.data),

  // campaigns
  listCampaigns: () =>
    http.get<{ campaigns: Campaign[]; audience_counts: { subscribers: number; customers: number } }>('/customer-portal/campaigns').then(r => r.data),
  createCampaign: (data: Partial<Campaign>) =>
    http.post<Campaign>('/customer-portal/campaigns', data).then(r => r.data),
  updateCampaign: (id: string, data: Partial<Campaign>) =>
    http.put<Campaign>(`/customer-portal/campaigns/${id}`, data).then(r => r.data),
  sendCampaign: (id: string) =>
    http.post<Campaign>(`/customer-portal/campaigns/${id}/send`).then(r => r.data),
  deleteCampaign: (id: string) => http.delete(`/customer-portal/campaigns/${id}`).then(r => r.data),
};

// Public (unauthenticated) — signup state + newsletter join.
export const publicPortalApi = {
  signupState: () => http.get<PublicSignupState>('/public/signup-state').then(r => r.data),
  newsletterSignup: (email: string, name?: string) =>
    http.post('/public/newsletter/signup', { email, name }).then(r => r.data),
};
